// 表情
import { notUseReferenceColorWeight, defaultBatchSize, defaultAspectRatios, defaultHD, defaultPerturb } from '../default';
import type { LayoutReferState } from '@/types/image';
import type { Tag } from '@/types/prompt';
import { getModelEventTrackType, mapModelSource, getStyleRefer, gethdFix } from '../index';
import { compressImage } from '@/utils/image';
import { useArtworkStore } from '@/store/artwork';
import { useUserStore } from '@/store/user';


export const module = ModuleId.Expression;

// 表情参数
export const expressionParams = {
  module,
  advancedMode: false, // 高级模式（已弃用）
  imgTags: [] as Tag[], // 图片提示词标签
  // prompt: '', // 用户输入的提示词
  imgTag: '', // 图像识别提示词
  // negativePrompt: '', // 反向提示词
  negativePromptWeight: 0.4, // 反向提示词权重
  layoutReferId: null as null | number, // 原始稿件图片id
  layoutRefer: null as null | LayoutReferState, // 原始稿件
  layoutReferPose: null as null | LayoutReferState, // 任务姿势图片
  layoutReferMode: 'draft' as 'draft' | 'pose', // 布局结构参考（draft: 草图，pose: 姿态）
  poseMode: 'norm' as 'norm' | 'myself', // 当 layoutReferMode 为姿势时（norm: 官方，myself: 我的）
  maskImage: null as null | LayoutReferState, // 原始稿件蒙板图片
  maskContrastImage: null as null | LayoutReferState, // 原始稿件蒙板反相图片
  sketchType: 1, // 布局参考图类型：1-线稿，2-色块
  layoutReferWeight: notUseReferenceColorWeight, // 布局参考图权重
  colorRefer: false, // 参考颜色
  styleReferList: [] as any[], // 形象样式参考图片
  styleReferType: 1, // 形象样式参考风格类型（1: 默认 2: 构图 3: 风格 4: 脸部）
  styleReferWeight: 0.4, // 形象样式参考权重
  // simpleBackground: true, // 简单画面背景
  // faceDetail: false, // 面部精修
  aspectRatios: '0:0', // 尺寸比例
  batchSize: defaultBatchSize, // 生成数量
  // hdFix: false, // 开启高清修复
  // hdScale: defaultHD, // 高清修复数值
  // enablePerturb: false, // 开启画面增强
  // perturb: defaultPerturb, // 画面增强
  // alchemy: false, // 语义增强引擎(Flux专用)
  imageGuidanceWeights: 2, // 画面指导权重
  colorSketchVer: 1, // 色块参考版本
  maskImgUrl: "", // 蒙板图片url
  originImgUrl: "", // 原图
  // 表情
  faceExpression: {
    id: "",
    name: "",
    prompt: ""
  },
}

// 将当前用户输入的生图参数转换成后端所需要
export async function getExpressionGenerateParam() {
  const artworkStore = useArtworkStore();
  const userStore = useUserStore();

  let params = {
    module: expressionParams.module,
    userId: userStore.userInfo.id,
    uniqueId: userStore.userInfo.uniqueId,
    artworkId: artworkStore.artworkState.id,
    modelList: artworkStore.artworkState.modelList,
    loraList: artworkStore.artworkState.modelList.map(item => {
      return {
        modelId: item.id,
        strength: item.strength
      }
    }),
    source: 1,
  } as any;

  params = {
    ...artworkStore.expression.params,
    ...params,
  }

  // 处理图片不超过 1024 * 1024
  if (params.layoutRefer) {
    params.layoutRefer = await compressImage(params.layoutRefer, 1024 * 1024);
    params.layoutReferId = params.layoutRefer.id;
    params.originImgUrl = params.layoutRefer.image;
  }

  // 处理图片蒙版不超过 1024 * 1024
  if (params.maskImage) {
    params.maskImage = await compressImage(params.maskImage, 1024 * 1024);
    
    const maskImgUrl = await generateMaskFromImage(params.maskImage.image);
    const remetoMaskImgUrl = await uploadFileUrlToAliyun(maskImgUrl);

    params.maskImgUrl = remetoMaskImgUrl;
  }

  params.positivePrompts = params.faceExpression?.prompt || "";

  return params;
}

import { useEventTracking } from '@/hooks/useEventTracking';
import type { ModelState } from '@/types/model';
import { startModuleGenerate } from './index';
import { useModelStore } from '@/store/model';
import { styleModelOptions } from '@/constants';
import { ModuleId } from '@xmiles/holopix-types';
import { generateMaskFromImage, getModuleLabelById } from '@xmiles/holopix-helper';
import { uploadFileUrlToAliyun } from '@/utils/aliyunUpload';

// 开启模块生图
export async function startExpressionGenerate(oldParams?: any, options?: {
  onClientIdGenerated?: (clientId: string) => void;
  onClientIdCleared?: () => void;
}) {
  const artworkStore = useArtworkStore();
  const modelStore = useModelStore();

  const params = await getExpressionGenerateParam();
  const module = expressionParams.module;

  const modelList = artworkStore.artworkState.modelList || [];
  const modelLabels = modelList.flatMap(item => item.label || []);
  // 按照 modelLabels 中 ID 的顺序过滤和排序标签
  const modelTagList = modelLabels
    .map((labelId) => modelStore.labelOptions.find((item: any) => item.value === labelId))
    .filter(Boolean)
    .map((item) => item!.label);

  const modelCategories = modelList.map((item) => item.category).filter(Boolean);

  const modelTypeList = modelCategories
    .map((categoryId) => modelStore.categoryOptions.find((item: any) => item.value === categoryId))
    .filter(Boolean)
    .map((item) => item!.label);

  const isUserGeneratedText = (() => {
    if (modelList.every(i => i.userId !== 0)) return '是';
    if (modelList.every(i => i.userId === 0)) return '否';
    return '混合';
  })();
  const modelIdList = params.modelList.map((item: ModelState) => item.id);
  const modelNameList = modelList.map((i: any) => i.modelName) || [];
  // 创建loraType映射关系
  const loraTypeMap = Object.fromEntries(styleModelOptions.map((option) => [option.value, option.label]));
  // 提取loraType对应的label组成数组
  const loraTypeLabels = modelList.map((item) => item.loraType ? loraTypeMap[item.loraType] || '视频模型' : item.loraType);



  startModuleGenerate(
    module,
    {
      getModuleParams: () => {
        return oldParams || params;
      },
      onStartTrack: (clientId) => {
        reportStartEventTracking(clientId);
      },
      onFinishTrack: (data) => {
        reportFinishEventTracking(data);
      },
      onClientIdGenerated: options?.onClientIdGenerated,
      onClientIdCleared: options?.onClientIdCleared,
    }
  );

  // 提交生图埋点
  function reportStartEventTracking(clientId: string) {

    const eventValue: any = {
      client_id: clientId,
      module: getModuleLabelById(module),
      source: params.source === 1 ? '直接生成' : '再迭代',
      artwork_id: artworkStore.artworkState.id.toString(),
      prompt: params.prompt,
      negative_prompt: params.negativePrompt,
      face_detail: params.faceDetail ? 1 : 0,
      simplebg: params.simpleBackground ? 1 : 0,
      style_refer: params.styleReferList?.length > 0 ? '1' : '0',
      batchSize: params.batchSize,
      // model_id: params.modelList.map((item: ModelState) => item.id).join(','),
      hp_model_id: String(modelIdList.join(',')),

      model_name: params.modelList.map((item: ModelState) => item.modelName).join(',') || JSON.stringify(params.modelList || ['--']),
      ckpt_type: params.modelList.map((item: ModelState) => item.ckptType).join(','),
      lora_type: params.modelList.map((item: ModelState) => item.loraType).join(','),
      is_personal_model: getModelEventTrackType(params.modelList),
      model_source: mapModelSource(getModelEventTrackType(params.modelList)),
      model_id: String(modelIdList.join(',')),
      is_team_share_works: artworkStore.artworkState.shareType === 1 ? 1 : 0,
      model_tags: modelTagList.join(','),
      model_type: modelTypeList.join(','),


      hp_model_id_list: modelIdList,
      hp_model_name_list: modelNameList,
      hp_model_tags_list: modelTagList,
      hp_model_type_list: modelTypeList,
      hp_is_user_generated: isUserGeneratedText,
      is_composite_model: loraTypeLabels
    }

    useEventTracking({
      event: 'creation_event',
      eventValue
    });

  }

  function reportFinishEventTracking({
    client_id = '',
    result = 0 as 0 | 1,
    imageIds = '',
    queue_time_number = 0,
    loading_time_number = 0,
    vipChannel = false
  } = {}) {
    const eventValue: any = {
      module: getModuleLabelById(module),
      client_id,
      source: params.source === 1 ? '直接生成' : '再迭代',
      img_id: imageIds,
      result,
      queue_time_number,
      loading_time_number,
      // model_id: params.modelList.map((item: ModelState) => item.id).join(','),
      hp_model_id: String(modelIdList.join(',')),
      hp_model_id_list: modelIdList,
      model_name: params.modelList.map((item: ModelState) => item.modelName).join(',') || JSON.stringify(params.modelList || ['--']),
      ckpt_type: params.modelList.map((item: ModelState) => item.ckptType).join(','),
      lora_type: params.modelList.map((item: ModelState) => item.loraType).join(','),
      first_model_ckpt: params.modelList[0]?.ckptType,
      vipChannel: vipChannel,
      is_personal_model: getModelEventTrackType(params.modelList),
      model_source: mapModelSource(getModelEventTrackType(params.modelList)),
      model_id: String(artworkStore.artworkState.modelList.map((item: ModelState) => item.id).join(',')),
      is_team_share_works: artworkStore.artworkState.shareType === 1 ? 1 : 0,

      prompt: params.prompt,
      negative_prompt: params.negativePrompt,
      face_detail: params.faceDetail ? 1 : 0,
      simplebg: params.simpleBackground ? 1 : 0,
      style_refer: params.styleReferList?.length > 0 ? '1' : '0',
      model_tags: modelTagList.join(','),
      hp_model_tags_list: modelTagList,
      model_type: modelTypeList.join(','),
      hp_model_type_list: modelTypeList,
      hp_is_user_generated: isUserGeneratedText
    }

    useEventTracking({
      event: 'artwork_status_event',
      eventValue
    });
  }
}